import { useMemo, useState, useCallback } from 'react';

import { PayoutType } from 'types';
import { Flex } from 'components/flex';
import { Info } from 'components/info';
import { mapQuery } from 'utils/query';
import { Payout } from 'store/api/payout';
import { Row, Col } from 'components/grid';
import { isMobile, isDesktop } from 'hooks';
import { formatPrice } from 'utils/helpers';
import { ListCard } from 'components/list-card';
import { useReportQuery } from 'store/api/event';
import { ListHeader } from 'components/list-header';
import { formatTimezoneSpecific } from 'utils/date';
import { EventPayment } from 'store/api/event/types';

import * as Styles from './styles';
import { BonusesDrawer } from './bonuses-drawer';
import { LocationRentDrawer } from './location-rent-drawer';
import { CategoryDetailsDrawer } from './category-details-drawer';
import { Expense, CardProps, EventProfitAndLossesProps } from './types';
import {
  commonProps,
  revenueTitles,
  expensesTitles,
  getSummaryInfo,
  getExpensesInfo,
  expensesTemplate,
  revenueCellsTablet,
  commonFooterCardProps,
  calculatePaymentMethods,
} from './config';

const Card = ({ title, children, minHeight, fullHeight = true }: CardProps) => (
  <Flex
    fullWidth
    minHeight={minHeight}
    flexDirection="column"
    fullHeight={fullHeight}
  >
    <h2>{title}</h2>
    {children}
  </Flex>
);

export const EventProfitAndLosses = ({
  event,
  profitAndLosses,
}: EventProfitAndLossesProps) => {
  const mobile = isMobile();
  const desktop = isDesktop();
  const [bonuses, setBonuses] = useState<Payout[]>();
  const [locationRent, setLocationRent] = useState<EventPayment[]>();
  const [selectedCategory, setSelectedCategory] = useState<string>();

  const { data, ...props } = useReportQuery(
    {
      id: String(event?.id),
    },
    { skip: !event || !event?.id }
  );

  const totalPaymentRevenue = useMemo(
    () => calculatePaymentMethods(data?.report),
    [data?.report]
  );

  const { summaryInfo, bonusesList, expensesInfo, categoryDetails } = useMemo(
    () => ({
      summaryInfo: getSummaryInfo(profitAndLosses),
      expensesInfo: getExpensesInfo(profitAndLosses),
      categoryDetails: profitAndLosses?.categoryDetails || [],
      bonusesList:
        profitAndLosses?.payoutItems?.filter((item) =>
          [PayoutType.Bonus, PayoutType.PrepaidBonus].includes(item.type)
        ) || [],
    }),
    [profitAndLosses]
  );

  const accountingExpenses = useMemo(
    () =>
      profitAndLosses?.accountingExpensesByCategory?.map((value) => ({
        ...value,
        withCategory: true,
      })) || [],
    [profitAndLosses?.accountingExpensesByCategory]
  );

  const expenses: Expense[] = useMemo(
    () => [...expensesInfo, ...accountingExpenses],
    [expensesInfo, accountingExpenses]
  );

  const handleClick = useCallback(
    (value: Expense) => {
      if (value?.withCategory) {
        setSelectedCategory(value.name);
        return;
      }

      if (value.name === 'Bonuses') {
        setBonuses(bonusesList);
        return;
      }

      if (value.name === 'Locations Rent') {
        setLocationRent(profitAndLosses?.locationRentPayments);
      }
    },
    [bonusesList, profitAndLosses?.locationRentPayments]
  );

  const expensesList = useMemo(() => {
    const clickableExpensesNames = ['Bonuses', 'Locations Rent'];

    return expenses?.map((value) => (
      <ListCard
        key={value?.name}
        headers={expensesTitles}
        tabletTemplate="1fr 1fr"
        template={expensesTemplate}
        values={[value?.name, formatPrice(value?.amount)]}
        onClick={
          value?.withCategory || clickableExpensesNames.includes(value.name)
            ? () => handleClick(value)
            : undefined
        }
      />
    ));
  }, [expenses, handleClick]);

  const expensesFooter = useMemo(
    () => (
      <ListCard
        {...commonFooterCardProps}
        tabletTemplate="1fr 1fr"
        template={expensesTemplate}
        headers={['', 'Total Amount']}
        values={[
          'Total Expenses',
          formatPrice(
            expenses?.reduce((acc, value) => acc + Number(value?.amount), 0)
          ),
        ]}
      />
    ),
    [expenses]
  );

  const revenueList = useMemo(
    () =>
      data?.report?.map((value) => (
        <ListCard
          key={value.date}
          headers={revenueTitles}
          values={[
            formatTimezoneSpecific(value?.checkoutDate, event?.timezone?.code),
            formatPrice(value?.paymentMethods?.check),
            formatPrice(value?.paymentMethods?.cash),
            formatPrice(value?.paymentMethods?.credit),
            formatPrice(value?.total?.tip),
          ]}
        />
      )),
    [data, event?.timezone?.code]
  );

  const revenueFooter = useMemo(
    () => (
      <ListCard
        {...commonFooterCardProps}
        headers={[''].concat(revenueTitles.slice(1))}
        cellsTablet={mobile ? undefined : revenueCellsTablet}
        values={[
          'Total Revenue',
          formatPrice(totalPaymentRevenue?.check),
          formatPrice(totalPaymentRevenue?.cash),
          formatPrice(totalPaymentRevenue?.credit),
          formatPrice(profitAndLosses?.totalTip),
        ]}
      />
    ),
    [mobile, totalPaymentRevenue, profitAndLosses?.totalTip]
  );

  return (
    <>
      <Flex
        gap={16}
        fullWidth
        fullHeight
        flexDirection={!desktop ? 'column' : 'row'}
      >
        <Flex column gap={16} fullWidth>
          <Card title="Summary" fullHeight={false}>
            <Row>
              {summaryInfo?.map((value, i) => (
                <Col col={2} key={i} colTablet={4} colMobile={6}>
                  <Info {...value} />
                </Col>
              ))}
            </Row>
          </Card>
          <Card minHeight={0} title="Revenue">
            <Styles.StyledList
              {...commonProps}
              {...mapQuery(props)}
              footer={revenueFooter}
              count={data?.report?.length}
              header={<ListHeader padding="8px" titles={revenueTitles} />}
            >
              {revenueList}
            </Styles.StyledList>
          </Card>
        </Flex>

        <Card title="Expenses">
          <Styles.StyledList
            {...commonProps}
            footer={expensesFooter}
            count={expenses?.length}
            header={
              <ListHeader
                padding="8px"
                titles={expensesTitles}
                template={expensesTemplate}
              />
            }
          >
            {expensesList}
          </Styles.StyledList>
        </Card>
      </Flex>

      <CategoryDetailsDrawer
        open={!!selectedCategory}
        categoryName={selectedCategory}
        categoryDetails={categoryDetails}
        onClose={() => setSelectedCategory(undefined)}
      />

      <BonusesDrawer
        bonuses={bonuses}
        open={!!bonuses?.length}
        onClose={() => setBonuses(undefined)}
      />

      <LocationRentDrawer
        opened={!!locationRent}
        locationRent={locationRent}
        onClose={() => setLocationRent(undefined)}
      />
    </>
  );
};
